import { apiErrorCode, apiErrorDataIndexes } from './apiErrorCode';
import { humanizeError } from './errors';
import { issueNumbers } from './quizCorrectGuard';

/**
 * Отказы ручек конструктора уроков (договор `docs/CONTRACTS/constructor-v2.md`
 * §0.1, §1.5, §3.2).
 *
 * ⚠️ РАЗЛИЧАТЬ ПРИЧИНЫ ТОЛЬКО ПО `code`. `humanizeError` судит по статусу, и для
 * содержательного отказа врёт: на 409 он говорит «такая запись уже существует»,
 * а на 400 показывает сырой текст сервера, только если тот короче 120 знаков.
 * Сервер нарочно держится в этих 120 знаках ради нынешнего конструктора —
 * но подпирать текст сервера длиной строки надёжнее один раз здесь.
 *
 * ⚠️ ТЕЛО ОТКАЗА ЛЕЖИТ В `raw` — см. шапку `apiErrorCode.ts`.
 */

const CODE_KEYS: Record<string, string> = {
  template_read_only: 'lessonBuilder.errTemplateReadOnly',
  invalid_block_content: 'lessonBuilder.errInvalidBlockContent',
  block_not_found: 'lessonBuilder.errBlockNotFound',
  section_not_found: 'lessonBuilder.errSectionNotFound',
  lesson_not_found: 'lessonBuilder.errLessonNotFound',
  // `quiz_no_correct_answer` здесь НЕТ намеренно: у него текст с номерами
  // вопросов, он собирается ниже подстановкой.
};

/** Разобранный отказ по одному блоку — то, что показывается на самом блоке. */
export type BlockFailure = {
  /** Машинный код, если сервер его прислал. */
  code?: string;
  /** Готовый текст для методиста. */
  text: string;
  /** Номера вопросов с нуля — по ним подсвечиваются сами вопросы в редакторе. */
  indexes?: number[];
};

/**
 * Разбор отказа записи блока.
 *
 * `t` передаётся снаружи, а не берётся из `i18n` напрямую: страница уже держит
 * `useTranslation`, и при смене языка текст должен перерисоваться вместе с ней.
 */
export function blockFailure(err: unknown, t: (key: string, vars?: Record<string, unknown>) => string): BlockFailure {
  const code = apiErrorCode(err);

  if (code === 'quiz_no_correct_answer') {
    const questions = apiErrorDataIndexes(err, 'questions');
    if (questions && questions.length > 0) {
      return { code, indexes: questions, text: t('lessonBuilder.errQuizNoCorrect', { numbers: issueNumbers(questions) }) };
    }
    const statements = apiErrorDataIndexes(err, 'statements');
    if (statements && statements.length > 0) {
      return { code, indexes: statements, text: t('lessonBuilder.errTfForeign', { numbers: issueNumbers(statements) }) };
    }
    // Код пришёл, а номеров нет: сервер новее или тело урезали по дороге.
    // Общий текст лучше молчания — методист хотя бы знает, где искать.
    return { code, text: t('lessonBuilder.errQuizNoCorrectPlain') };
  }

  const key = code ? CODE_KEYS[code] : undefined;
  return { code, text: key ? t(key) : humanizeError(err) };
}
